import { SeoHead } from "@/components/SeoHead";
import { getSeoCopy } from "@/i18n/seoCopy";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import Navigation from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { ReservedImageSlot } from "@/components/bath/ReservedImageSlot";
import { BathLeadForm } from "@/components/bath/BathLeadForm";
import { MaterialsQuoteForm } from "@/components/bath/MaterialsQuoteForm";
import { LocalizedLink } from "@/components/LocalizedLink";
import { CheckCircle2, ShieldCheck, Truck, Layers, Building2, Ruler } from "lucide-react";
import { useI18n } from "@/i18n";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const BathSanitary = () => {
  const { t, language } = useI18n();
  const seo = getSeoCopy("bathSanitary", language);

  type CollectionItem = {
    slug: string;
    name: string;
    code: string;
    tagline: string;
    slotLabel: string;
    images: string[];
    keySpecs: { label: string; value: string }[];
    bestFor: string;
  };

  type CollectionCategory = {
    id: string;
    label: string;
    title: string;
    intro: string;
    items: CollectionItem[];
  };

  const categories: CollectionCategory[] = [
    {
      id: "freestanding-bathtubs",
      label: "Category 01",
      title: "Freestanding Solid Surface Bathtubs",
      intro: "Sculpted, monolithic stone-resin bathtubs cast as a single seamless form. Architectural focal points for primary suites and signature villa bathrooms.",
      items: [
        {
          slug: "aria-sculpt", code: "ARIA-SCULPT-1700", name: "ARIA Sculpt Bathtub",
          tagline: "Fluted freestanding · solid surface",
          slotLabel: "Hero / Featured Bathtub",
          images: ["/images/bath/aria-sculpt-hero.jpg", "/images/bath/aria-sculpt-detail.jpg", "/images/bath/aria-sculpt-top.jpg"],
          keySpecs: [
            { label: "Length", value: "1700 mm" },
            { label: "Material", value: "Solid Surface" },
            { label: "Style", value: "Fluted" },
          ],
          bestFor: "Primary suites · branded residences",
        },
        {
          slug: "aria-monolith", code: "ARIA-MONOLITH-1750", name: "ARIA Monolith Tub",
          tagline: "Architectural oval · solid surface",
          slotLabel: "Bathtub Variant 2",
          images: ["/images/bath/aria-monolith.jpg", "/images/bath/aria-monolith-detail.jpg", "/images/bath/aria-monolith-villa.jpg"],
          keySpecs: [
            { label: "Length", value: "1750 mm" },
            { label: "Material", value: "Solid Surface" },
            { label: "Style", value: "Oval Monolith" },
          ],
          bestFor: "Villas · hotel guest suites",
        },
        {
          slug: "aria-diamond", code: "ARIA-DIAMOND-1700", name: "ARIA Diamond Bathtub",
          tagline: "Diamond-texture · solid surface",
          slotLabel: "Bathtub Variant 3 — Diamond Texture",
          images: ["/images/bath/aria-diamond-hero.jpg", "/images/bath/aria-diamond-detail.jpg", "/images/bath/aria-diamond-suite.jpg"],
          keySpecs: [
            { label: "Length", value: "1700 mm" },
            { label: "Material", value: "Solid Surface" },
            { label: "Style", value: "Diamond Texture" },
          ],
          bestFor: "Boutique hotels · editorial bathrooms",
        },
        {
          slug: "vela-freestanding", code: "VELA-SP1836-1870", name: "VELA Freestanding Bathtub",
          tagline: "Slim-rim acrylic · stainless support",
          slotLabel: "Freestanding · Acrylic Bathtub",
          images: ["/images/bath/sp1836/hero.jpg", "/images/bath/sp1836/angle.jpg", "/images/bath/sp1836/lifestyle.jpg"],
          keySpecs: [
            { label: "Length", value: "1870 mm" },
            { label: "Material", value: "PMMA Acrylic" },
            { label: "Style", value: "Slim-Rim" },
          ],
          bestFor: "Wellness suites · short-stay rentals",
        },
      ],
    },
    {
      id: "whirlpool-jacuzzi",
      label: "Category 02",
      title: "Whirlpool & Jacuzzi Spa Bathtubs",
      intro: "Hydro-massage acrylic bathtubs with integrated jet systems, LED chromotherapy and digital controls. Engineered for hospitality wellness installations.",
      items: [
        {
          slug: "rem7-whirlpool-deluxe", code: "REM7-HYDRA-DLX-1700", name: "REM7 Hydra Deluxe Whirlpool",
          tagline: "Touch control · ozone · FM radio · waterfall",
          slotLabel: "Whirlpool · Deluxe Spa Bathtub",
          images: ["/images/bath/whirlpool-deluxe/hero.jpg", "/images/bath/whirlpool-deluxe/controls.jpg", "/images/bath/whirlpool-deluxe/lifestyle.jpg"],
          keySpecs: [
            { label: "Length", value: "1700 mm" },
            { label: "Jets", value: "16 hydro + 16 LED bubble" },
            { label: "Control", value: "Digital Touch" },
          ],
          bestFor: "5★ hotels · primary suites",
        },
        {
          slug: "rem7-whirlpool", code: "REM7-HYDRA-1850", name: "REM7 Hydra Whirlpool",
          tagline: "Acrylic whirlpool · LED chromotherapy",
          slotLabel: "Whirlpool · Spa Bathtub",
          images: ["/images/bath/whirlpool/hero.jpg", "/images/bath/whirlpool/jets.jpg", "/images/bath/whirlpool/lifestyle.jpg"],
          keySpecs: [
            { label: "Length", value: "1850 mm" },
            { label: "Pump", value: "1.5 HP" },
            { label: "Jets", value: "16 hydro" },
          ],
          bestFor: "Couples spa · primary suites",
        },
        {
          slug: "rem7-whirlpool-compact", code: "REM7-HYDRA-CPT-1800", name: "REM7 Hydra Compact",
          tagline: "Compact 1800mm · single-bather spa",
          slotLabel: "Whirlpool · Compact Spa Bathtub",
          images: ["/images/bath/whirlpool-compact/hero.jpg", "/images/bath/whirlpool-compact/jets.jpg", "/images/bath/whirlpool-compact/lifestyle.jpg"],
          keySpecs: [
            { label: "Length", value: "1800 mm" },
            { label: "Pump", value: "1.0 HP" },
            { label: "Apron", value: "2-side integral" },
          ],
          bestFor: "Apartments · compact wellness",
        },
        {
          slug: "rem7-whirlpool-surf", code: "REM7-HYDRA-SRF-1500", name: "REM7 Hydra Surf Corner",
          tagline: "1500mm corner · 800mm waterfall",
          slotLabel: "Whirlpool · Corner Spa Bathtub",
          images: ["/images/bath/whirlpool-surf/hero.jpg", "/images/bath/whirlpool-surf/waterfall.jpg", "/images/bath/whirlpool-surf/lifestyle.jpg"],
          keySpecs: [
            { label: "Footprint", value: "1500 × 1500 mm" },
            { label: "Jets", value: "10 surf" },
            { label: "Feature", value: "800mm waterfall" },
          ],
          bestFor: "Corner installs · hospitality",
        },
      ],
    },
    {
      id: "washbasins-vanities",
      label: "Category 03",
      title: "Washbasins & Vanity Consoles",
      intro: "Sculpted solid surface basins and integrated vanity consoles. Designed to coordinate with the ARIA bathtub line for a complete suite.",
      items: [
        {
          slug: "vena-basin", code: "VENA-BASIN-600", name: "VENA Counter Basin",
          tagline: "Sculpted solid surface washbasin",
          slotLabel: "Washbasin 1",
          images: ["/images/bath/vena-basin.jpg", "/images/bath/vena-basin-detail.jpg", "/images/bath/vena-basin-suite.jpg"],
          keySpecs: [
            { label: "Width", value: "600 mm" },
            { label: "Material", value: "Solid Surface" },
            { label: "Mount", value: "Counter-top" },
          ],
          bestFor: "Powder rooms · vanity coordination",
        },
        {
          slug: "vena-vanity", code: "VENA-VANITY-1200", name: "VENA Vanity Console",
          tagline: "Integrated bathroom vanity solution",
          slotLabel: "Vanity 1",
          images: ["/images/bath/vena-vanity.jpg", "/images/bath/vena-vanity-detail.jpg", "/images/bath/vena-vanity-suite.jpg"],
          keySpecs: [
            { label: "Width", value: "1200 mm" },
            { label: "Material", value: "Solid Surface" },
            { label: "Mount", value: "Wall-hung" },
          ],
          bestFor: "Hotel guest baths · primary suites",
        },
      ],
    },
  ];

  const collection = categories.flatMap((c) => c.items);
  const totalCount = collection.length;

  const [activeFilter, setActiveFilter] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState<string>("");

  const normalizedQuery = searchQuery.trim().toLowerCase();
  const matchesQuery = (item: CollectionItem) => {
    if (!normalizedQuery) return true;
    const haystack = [
      item.name,
      item.code,
      item.tagline,
      item.bestFor,
      ...item.keySpecs.flatMap((s) => [s.label, s.value]),
    ]
      .join(" ")
      .toLowerCase();
    return haystack.includes(normalizedQuery);
  };

  const baseCategories =
    activeFilter === "all" ? categories : categories.filter((c) => c.id === activeFilter);
  const visibleCategories = baseCategories
    .map((c) => ({ ...c, items: c.items.filter(matchesQuery) }))
    .filter((c) => c.items.length > 0);
  const visibleCount = visibleCategories.reduce((n, c) => n + c.items.length, 0);

  return (
    <div className="min-h-screen bg-white text-neutral-900">
      <SeoHead
        route="bathSanitary"
        schema={{
          "@context": "https://schema.org",
          "@type": "ItemList",
          name: "Aureum VII Trading Bath & Sanitary Collection",
          description: seo.description,
          itemListElement: collection.map((p, i) => ({
            "@type": "ListItem",
            position: i + 1,
            item: { "@type": "Product", name: p.name, description: p.tagline, brand: { "@type": "Brand", name: "Aureum VII Trading" } },
          })),
        }}
      />

      <Navigation />

      {/* HERO */}
      <section className="border-b border-neutral-200 bg-white pt-32 pb-20">
        <div className="container mx-auto grid gap-12 px-6 lg:grid-cols-2 lg:items-center lg:px-12">
          <div>
            <p className="mb-4 font-mono text-xs uppercase tracking-[0.3em] text-amber-600">
              For Developers · Architects · Designers
            </p>
            <h1 className="font-serif text-4xl font-bold leading-tight text-neutral-900 sm:text-5xl lg:text-6xl">
              {seo.h1}
            </h1>
            <div className="mt-4 h-px w-20 bg-amber-500" />
            <p className="mt-6 max-w-xl text-lg text-neutral-600 leading-relaxed">
              Designed for architects, developers, and premium residential projects across Europe.
              Consistent supply, MOQ-ready, custom branding available.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button asChild size="lg" className="bg-neutral-900 px-8 text-white hover:bg-neutral-800">
                <a href="#quote">CONTACT SALES</a>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-neutral-900 bg-transparent text-neutral-900 hover:bg-neutral-100">
                <a href="#quote">Speak to a Project Specialist</a>
              </Button>
            </div>
            <p className="mt-6 text-xs text-neutral-500">CE Certified · Solid Surface · OEM-ready</p>
          </div>
          <div className="relative">
            <ReservedImageSlot
              src="/images/bath/aria-sculpt-hero.jpg"
              alt="ARIA Sculpt freestanding solid surface bathtub modern design"
              aspect="aspect-[4/5]"
              slotLabel="Hero — Freestanding fluted bathtub"
              priority
            />
          </div>
        </div>
      </section>

      {/* FEATURED PRODUCT */}
      <section className="border-b border-neutral-200 bg-neutral-50 py-24">
        <div className="container mx-auto grid gap-12 px-6 lg:grid-cols-2 lg:items-center lg:px-12">
          <div className="order-2 lg:order-1">
            <p className="mb-3 font-mono text-xs uppercase tracking-[0.3em] text-amber-600">
              Featured · Flagship Bathtub
            </p>
            <h2 className="font-serif text-3xl font-bold text-neutral-900 sm:text-4xl">ARIA Sculpt Bathtub</h2>
            <div className="mt-3 h-px w-16 bg-amber-500" />
            <p className="mt-4 text-neutral-700 leading-relaxed">
              A freestanding solid surface bathtub crafted with precision-machined fluting and a
              seamless monolithic silhouette. Engineered for high-end residential developments,
              boutique hotels, and signature architectural projects.
            </p>

            <dl className="mt-8 grid grid-cols-2 gap-6 border-y border-neutral-200 py-6">
              <div>
                <dt className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">Material</dt>
                <dd className="mt-1 text-neutral-900">Solid Surface (Stone Resin)</dd>
              </div>
              <div>
                <dt className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">Finish</dt>
                <dd className="mt-1 text-neutral-900">Matte / Gloss</dd>
              </div>
              <div>
                <dt className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">Installation</dt>
                <dd className="mt-1 text-neutral-900">Freestanding</dd>
              </div>
              <div>
                <dt className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">Certification</dt>
                <dd className="mt-1 inline-flex items-center gap-2 text-neutral-900">
                  <ShieldCheck className="h-4 w-4 text-amber-600" /> CE Certified
                </dd>
              </div>
            </dl>

            <div className="mt-8 flex flex-wrap gap-3">
              <Button asChild size="lg" className="bg-neutral-900 text-white hover:bg-neutral-800">
                <a href="#quote">Request Quote for Bulk Orders</a>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-neutral-300 text-neutral-900 hover:bg-neutral-100">
                <LocalizedLink to="/bath-sanitary/aria-sculpt">View Product Page →</LocalizedLink>
              </Button>
            </div>
            <p className="mt-4 text-xs text-neutral-500">Bulk pricing available upon request.</p>
          </div>
          <div className="order-1 lg:order-2">
            <ReservedImageSlot
              src="/images/bath/aria-sculpt-detail.jpg"
              alt="freestanding solid surface bathtub luxury Europe"
              aspect="aspect-square"
              slotLabel="Featured Detail Shot"
            />
          </div>
        </div>
      </section>

      {/* B2B VALUE */}
      <section className="border-b border-neutral-200 bg-white py-24">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="mx-auto max-w-3xl text-center">
            <p className="mb-3 font-mono text-xs uppercase tracking-[0.3em] text-amber-600">B2B</p>
            <h2 className="font-serif text-3xl font-bold text-neutral-900 sm:text-4xl">
              Built for Developers, Architects &{" "}
              <span className="text-amber-600">Interior Designers</span>
            </h2>
            <div className="mx-auto mt-3 h-px w-16 bg-amber-500" />
            <p className="mt-4 text-neutral-600">
              Premium aesthetic at competitive cost. Reliable supply for projects of any scale.
            </p>
          </div>

          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {[
              { icon: Building2, title: "Residential & Hospitality", body: "Ideal for residential developments, hotels, and luxury apartments." },
              { icon: Layers, title: "MOQ-Ready Supply", body: "Consistent supply for large-scale projects with predictable lead times." },
              { icon: Ruler, title: "Custom Branding / OEM", body: "White-label and OEM available for property brands and design studios." },
              { icon: Truck, title: "Fast European Logistics", body: "Direct shipping across the EU. Air freight options for urgent timelines." },
              { icon: ShieldCheck, title: "CE Certified Quality", body: "Compliant with European standards for solid surface sanitary ware." },
              { icon: CheckCircle2, title: "Premium at Trade Cost", body: "Five-star aesthetic without five-star construction overhead." },
            ].map((f) => (
              <div key={f.title} className="border border-neutral-200 bg-neutral-50 p-6 transition-colors hover:border-amber-500">
                <f.icon className="h-6 w-6 text-amber-600" />
                <h3 className="mt-4 font-serif text-lg font-semibold text-neutral-900">{f.title}</h3>
                <p className="mt-2 text-sm text-neutral-600 leading-relaxed">{f.body}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 flex flex-wrap justify-center gap-3">
            <Button asChild size="lg" className="bg-neutral-900 text-white hover:bg-neutral-800">
              <a href="#quote">Request Trade Access</a>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-neutral-900 text-neutral-900 hover:bg-neutral-100">
              <a href="#quote">Submit Project Requirements</a>
            </Button>
          </div>
        </div>
      </section>

      {/* COLLECTION — CATEGORIZED */}
      <section id="collection" className="border-b border-neutral-200 bg-neutral-50 py-24">
        <div className="container mx-auto px-6 lg:px-12">
          {/* Section header + category navigation */}
          <div className="mb-10 flex flex-wrap items-end justify-between gap-6">
            <div>
              <p className="mb-3 font-mono text-xs uppercase tracking-[0.3em] text-amber-600">Catalogue</p>
              <h2 className="font-serif text-3xl font-bold text-neutral-900 sm:text-4xl">
                Bath &amp; Sanitary Range — by Category
              </h2>
              <div className="mt-3 h-px w-16 bg-amber-500" />
              <p className="mt-3 max-w-2xl text-neutral-600 leading-relaxed">
                Organised by product family so specifiers can compare like with like. Each card
                lists the SKU, key dimensions, material and best-fit application — tap any item
                to open its full technical page.
              </p>
            </div>
            <LocalizedLink to="/bath-sanitary/image-status" className="text-sm font-medium text-amber-600 hover:text-amber-700">
              Check bath image status →
            </LocalizedLink>
          </div>

          {/* Sticky category filter bar */}
          <div className="sticky top-20 z-30 -mx-6 mb-12 border-y border-neutral-200 bg-white/95 px-6 py-3 shadow-sm backdrop-blur lg:-mx-12 lg:px-12">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex flex-wrap items-center gap-2">
                <span className="mr-2 font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">
                  Filter:
                </span>
                <button
                  type="button"
                  onClick={() => setActiveFilter("all")}
                  aria-pressed={activeFilter === "all"}
                  className={`inline-flex items-center gap-2 border px-4 py-2 text-xs font-medium transition-colors ${
                    activeFilter === "all"
                      ? "border-amber-500 bg-neutral-900 text-white"
                      : "border-neutral-300 bg-white text-neutral-700 hover:border-amber-500 hover:text-amber-600"
                  }`}
                >
                  <span>All Products</span>
                  <span className="text-neutral-400">({totalCount})</span>
                </button>
                {categories.map((c) => {
                  const active = activeFilter === c.id;
                  return (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => setActiveFilter(c.id)}
                      aria-pressed={active}
                      className={`inline-flex items-center gap-2 border px-4 py-2 text-xs font-medium transition-colors ${
                        active
                          ? "border-amber-500 bg-neutral-900 text-white"
                          : "border-neutral-300 bg-white text-neutral-700 hover:border-amber-500 hover:text-amber-600"
                      }`}
                    >
                      <span className={`font-mono text-[10px] uppercase tracking-[0.2em] ${active ? "text-amber-300" : "text-amber-600"}`}>
                        {c.label}
                      </span>
                      <span className="text-neutral-300">·</span>
                      <span>{c.title}</span>
                      <span className={active ? "text-neutral-300" : "text-neutral-400"}>({c.items.length})</span>
                    </button>
                  );
                })}
              </div>

              {/* Search box */}
              <div className="relative lg:w-72">
                <label htmlFor="bath-product-search" className="sr-only">
                  Search products
                </label>
                <svg
                  aria-hidden="true"
                  viewBox="0 0 20 20"
                  fill="none"
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400"
                >
                  <circle cx="9" cy="9" r="6" stroke="currentColor" strokeWidth="1.5" />
                  <path d="m17 17-3.5-3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
                <input
                  id="bath-product-search"
                  type="search"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by name, SKU, spec…"
                  className="w-full border border-neutral-300 bg-white py-2 pl-9 pr-9 text-xs text-neutral-800 placeholder:text-neutral-400 focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500"
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery("")}
                    aria-label="Clear search"
                    className="absolute right-2 top-1/2 -translate-y-1/2 px-1 text-neutral-400 hover:text-neutral-700"
                  >
                    ×
                  </button>
                )}
              </div>
            </div>
            {normalizedQuery && (
              <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">
                {visibleCount} match{visibleCount === 1 ? "" : "es"} for "{searchQuery}"
              </p>
            )}
          </div>

          {/* Categories */}
          <div className="space-y-20">
            {visibleCategories.length === 0 && (
              <div className="border border-dashed border-neutral-300 bg-white p-10 text-center">
                <p className="font-serif text-lg text-neutral-700">No products match your search.</p>
                <p className="mt-2 text-sm text-neutral-500">
                  Try a different keyword, SKU, or{" "}
                  <button
                    type="button"
                    onClick={() => {
                      setSearchQuery("");
                      setActiveFilter("all");
                    }}
                    className="font-medium text-amber-600 hover:text-amber-700"
                  >
                    reset filters
                  </button>
                  .
                </p>
              </div>
            )}
            {visibleCategories.map((cat) => (
              <div key={cat.id} id={cat.id} className="scroll-mt-28">
                {/* Category header */}
                <div className="mb-8 border-l-4 border-amber-500 pl-5">
                  <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-amber-600">{cat.label}</p>
                  <h3 className="mt-1 font-serif text-2xl font-bold text-neutral-900 sm:text-3xl">{cat.title}</h3>
                  <p className="mt-2 max-w-3xl text-sm text-neutral-600 leading-relaxed">{cat.intro}</p>
                </div>

                {/* Items grid */}
                <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
                  {cat.items.map((p, idx) => (
                    <LocalizedLink
                      key={p.slug}
                      to={`/bath-sanitary/${p.slug}`}
                      className="group flex flex-col overflow-hidden border border-neutral-200 bg-white transition-all hover:-translate-y-0.5 hover:border-amber-500 hover:shadow-lg"
                      aria-label={`View ${p.name} (${p.code}) product page`}
                    >
                      {/* Index badge over hero image */}
                      <div className="relative">
                        <ReservedImageSlot
                          src={p.images[0]}
                          alt={`${p.name} — ${p.tagline}`}
                          aspect="aspect-[4/5]"
                          slotLabel={p.slotLabel}
                        />
                        <span className="absolute left-3 top-3 rounded-sm bg-neutral-900/85 px-2 py-1 font-mono text-[10px] uppercase tracking-[0.18em] text-amber-300">
                          {cat.label.replace("Category ", "")}.{String(idx + 1).padStart(2, "0")}
                        </span>
                        <span className="absolute right-3 top-3 rounded-sm border border-amber-500/40 bg-white/90 px-2 py-1 font-mono text-[10px] tracking-wider text-neutral-700">
                          {p.code}
                        </span>
                      </div>

                      {/* Thumbnails */}
                      <div className="grid grid-cols-3 gap-px bg-neutral-200">
                        {p.images.slice(0, 3).map((src, i) => (
                          <div key={src} className="relative aspect-square overflow-hidden bg-neutral-100">
                            <img
                              src={src}
                              alt=""
                              loading="lazy"
                              decoding="async"
                              className="absolute inset-0 h-full w-full object-cover opacity-90 transition group-hover:opacity-100"
                              onError={(e) => {
                                (e.currentTarget as HTMLImageElement).style.opacity = "0.2";
                              }}
                            />
                            {i === 2 && p.images.length > 3 && (
                              <span className="absolute inset-0 flex items-center justify-center bg-black/60 font-mono text-[10px] uppercase tracking-[0.15em] text-white">
                                +{p.images.length - 3}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>

                      {/* Card body */}
                      <div className="flex flex-1 flex-col p-4">
                        <h4 className="font-serif text-lg font-semibold text-neutral-900 group-hover:text-amber-600">
                          {p.name}
                        </h4>
                        <p className="text-sm text-neutral-500">{p.tagline}</p>

                        {/* Key specs strip */}
                        <dl className="mt-4 space-y-1 border-t border-neutral-100 pt-3 text-xs">
                          {p.keySpecs.map((s) => (
                            <div key={s.label} className="flex justify-between gap-3">
                              <dt className="font-mono uppercase tracking-wider text-neutral-400">{s.label}</dt>
                              <dd className="text-right text-neutral-700">{s.value}</dd>
                            </div>
                          ))}
                        </dl>

                        {/* Best for */}
                        <p className="mt-3 border-t border-neutral-100 pt-3 text-[11px] italic text-neutral-500">
                          Best for: {p.bestFor}
                        </p>

                        <p className="mt-auto pt-4 text-xs font-medium text-amber-600">
                          View full specs →
                        </p>
                      </div>
                    </LocalizedLink>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-12 border border-dashed border-neutral-300 bg-white p-4 text-center text-xs text-neutral-500">
            Coming soon: Smart Toilets · Wall-Hung Sanitaryware · Shower Solutions
            <span className="mx-2 text-neutral-300">·</span>
            <LocalizedLink to="/bath-sanitary/image-status" className="text-amber-600 hover:text-amber-700">
              Image upload status
            </LocalizedLink>
          </div>
        </div>
      </section>

      {/* MATERIALS */}
      <section className="border-b border-neutral-200 bg-white py-24">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="mx-auto max-w-3xl text-center">
            <p className="mb-3 font-mono text-xs uppercase tracking-[0.3em] text-amber-600">Materials</p>
            <h2 className="font-serif text-3xl font-bold text-neutral-900 sm:text-4xl">
              What Our Bathtubs & Basins Are Made Of
            </h2>
            <div className="mx-auto mt-3 h-px w-16 bg-amber-500" />
            <p className="mt-4 text-neutral-600 leading-relaxed">
              Every ARIA and VENA piece is crafted from premium <strong>Solid Surface (Stone Resin)</strong> —
              an engineered composite of natural mineral fillers (≈66% ATH — Aluminium Tri-Hydrate)
              bonded with high-grade acrylic or polyester resin and color pigments. The result is a
              non-porous, seamless, hygienic surface used by the world's leading luxury sanitaryware brands.
            </p>
          </div>

          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {[
              { title: "Composition", body: "≈66% Aluminium Tri-Hydrate (ATH) mineral + acrylic/polyester resin + pigments. Cast and cured into a solid monolithic block." },
              { title: "Non-Porous & Hygienic", body: "Zero water absorption. Resists mold, bacteria, and stains — ideal for hotels, residences and wellness projects." },
              { title: "Warm to the Touch", body: "Unlike ceramic or stone, solid surface retains heat — bathwater stays warmer for longer." },
              { title: "Repairable Surface", body: "Scratches, chips and dull spots can be sanded and polished out on-site, restoring a like-new finish." },
              { title: "Seamless & Sculptural", body: "Thermoformable — allows fluted, curved and monolithic shapes impossible in ceramic or cast iron." },
              { title: "CE Certified", body: "Compliant with European standards for solid surface sanitary ware. Food-safe and fire-rated grades available." },
            ].map((f) => (
              <div key={f.title} className="border border-neutral-200 bg-neutral-50 p-6">
                <h3 className="font-serif text-lg font-semibold text-neutral-900">{f.title}</h3>
                <p className="mt-2 text-sm text-neutral-600 leading-relaxed">{f.body}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 overflow-hidden border border-neutral-200">
            <table className="w-full text-left text-sm">
              <thead className="bg-neutral-900 text-white">
                <tr>
                  <th className="p-4 font-mono text-xs uppercase tracking-[0.15em]">Property</th>
                  <th className="p-4 font-mono text-xs uppercase tracking-[0.15em]">Specification</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-200 bg-white">
                <tr><td className="p-4 font-medium text-neutral-900">Material</td><td className="p-4 text-neutral-600">Solid Surface — Modified Acrylic / Stone Resin</td></tr>
                <tr><td className="p-4 font-medium text-neutral-900">Mineral Content</td><td className="p-4 text-neutral-600">≈66% ATH (Aluminium Tri-Hydrate)</td></tr>
                <tr><td className="p-4 font-medium text-neutral-900">Water Absorption</td><td className="p-4 text-neutral-600">&lt; 0.04% (non-porous)</td></tr>
                <tr><td className="p-4 font-medium text-neutral-900">Surface Finish</td><td className="p-4 text-neutral-600">Matte or Gloss — repairable on-site</td></tr>
                <tr><td className="p-4 font-medium text-neutral-900">Wall Thickness</td><td className="p-4 text-neutral-600">8–12 mm (bathtubs) · 10–15 mm (basins)</td></tr>
                <tr><td className="p-4 font-medium text-neutral-900">Standards</td><td className="p-4 text-neutral-600">CE · ISO 9001 · cUPC available on request</td></tr>
                <tr><td className="p-4 font-medium text-neutral-900">Warranty</td><td className="p-4 text-neutral-600">10 years (structural) · 2 years (surface)</td></tr>
              </tbody>
            </table>
          </div>

          {/* BEST PRICE / MOQ CTA */}
          <div className="mt-12 border-2 border-amber-500 bg-gradient-to-br from-amber-50 to-white p-8 lg:p-12">
            <div className="grid gap-8 lg:grid-cols-3 lg:items-center">
              <div className="lg:col-span-2">
                <p className="mb-3 font-mono text-xs uppercase tracking-[0.3em] text-amber-600">Best Price Guarantee · MOQ Ready</p>
                <h3 className="font-serif text-2xl font-bold text-neutral-900 sm:text-3xl">
                  We secure the best solid surface price on the market.
                </h3>
                <p className="mt-4 text-neutral-700 leading-relaxed">
                  As a direct B2B trading partner, we cut out distributor markups and source straight
                  from certified solid surface manufacturers. Tell us your project — model, finish,
                  quantities and timeline — and we'll secure the lowest MOQ and the most competitive
                  per-unit price for your development.
                </p>
                <ul className="mt-4 grid gap-2 text-sm text-neutral-700 sm:grid-cols-2">
                  <li className="flex items-start gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" /> Factory-direct pricing</li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" /> Flexible MOQ for projects</li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" /> Custom OEM / branding</li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" /> EU sea & air freight</li>
                </ul>
              </div>
              <div className="flex flex-col gap-3">
                <Button asChild size="lg" className="bg-neutral-900 text-white hover:bg-neutral-800">
                  <a href="#quote">Request Best Price & MOQ</a>
                </Button>
                <Button asChild size="lg" variant="outline" className="border-neutral-900 text-neutral-900 hover:bg-neutral-100">
                  <a href="https://wa.me/34642278939?text=Hello%20Aureum%20VII%2C%20I%27d%20like%20a%20bulk%20quote%20for%20solid%20surface%20bathware." target="_blank" rel="noopener noreferrer">
                    WhatsApp a Specialist
                  </a>
                </Button>
                <p className="text-center text-xs text-neutral-500">Reply within 1 business day</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MATERIALS FAQ */}
      <section className="border-b border-neutral-200 bg-neutral-50 py-24">
        <div className="container mx-auto max-w-4xl px-6 lg:px-12">
          <div className="mb-10 text-center">
            <p className="mb-3 font-mono text-xs uppercase tracking-[0.3em] text-amber-600">Materials FAQ</p>
            <h2 className="font-serif text-3xl font-bold text-neutral-900 sm:text-4xl">
              Durability, Composition & Maintenance
            </h2>
            <div className="mx-auto mt-3 h-px w-16 bg-amber-500" />
            <p className="mt-4 text-neutral-600">Everything specifiers ask about our solid surface bathware.</p>
          </div>

          <Accordion type="single" collapsible className="space-y-3">
            {[
              { q: "What exactly is solid surface?", a: "Solid surface is an engineered composite of approximately 66% natural mineral filler (Aluminium Tri-Hydrate / ATH) bonded with high-grade acrylic or polyester resin and color pigments. It is cast and cured into a solid, monolithic, non-porous block — the same material category as Corian®, HI-MACS® and Staron®." },
              { q: "How durable is it compared to ceramic, acrylic or cast iron?", a: "Solid surface is denser and warmer than acrylic, far less brittle than ceramic (it doesn't chip when struck), and significantly lighter than cast iron — making installation easier on upper floors. It carries a 10-year structural warranty under normal residential and hospitality use." },
              { q: "Is the surface hygienic?", a: "Yes. With water absorption below 0.04%, the surface is non-porous: bacteria, mold and mildew cannot penetrate. There are no joints or seams where grime can accumulate. It's the standard specification for medical, hospitality and luxury wellness environments." },
              { q: "Can scratches or stains be repaired?", a: "Yes — this is one of the material's biggest advantages. Light scratches, dull spots and most stains can be sanded out and re-polished on-site with fine abrasive pads, restoring a like-new finish. No replacement needed." },
              { q: "How do I clean it day-to-day?", a: "Wipe with warm soapy water or a mild non-abrasive bathroom cleaner and a soft cloth. Avoid bleach, acetone, paint stripper, oven cleaner or abrasive scouring pads. For matte finishes, a white non-abrasive pad refreshes the surface." },
              { q: "Is it heat and stain resistant?", a: "Resistant to everyday hot water, cosmetics, hair dye and household chemicals when wiped promptly. Avoid placing items above 180 °C directly on the surface. Hair dye and tannin stains should be cleaned within a few hours." },
              { q: "What thickness and weight should I expect?", a: "Bathtubs: 8–12 mm wall thickness, typically 75–110 kg depending on size. Basins: 10–15 mm wall thickness, 12–35 kg. Lighter than cast iron or natural stone, so no structural reinforcement is normally required." },
              { q: "Is it certified for European projects?", a: "Yes — CE certified and ISO 9001 manufactured. cUPC, REACH and fire-rated grades are available on request for hospitality and public-sector projects." },
              { q: "What is the warranty?", a: "10 years on structural integrity and 2 years on surface finish under normal use. Full warranty terms are available in our downloadable warranty PDF." },
              { q: "Can finishes and colors be customised?", a: "Yes. Standard finishes are matte and gloss in white. Custom RAL colors, textured finishes (fluted, diamond, ribbed) and OEM branding are available from defined MOQs — typically from 20–50 units depending on the model." },
            ].map((item, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="border border-neutral-200 bg-white px-5">
                <AccordionTrigger className="text-left font-serif text-base font-semibold text-neutral-900 hover:text-amber-600">
                  {item.q}
                </AccordionTrigger>
                <AccordionContent className="text-neutral-600 leading-relaxed">
                  {item.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>

          <div className="mt-10 flex flex-wrap justify-center gap-3">
            <Button asChild size="lg" className="bg-neutral-900 text-white hover:bg-neutral-800">
              <a href="#quote">Ask a Material Question</a>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-neutral-900 text-neutral-900 hover:bg-neutral-100">
              <a href="/files/Aureum7_Warranty.pdf" target="_blank" rel="noopener noreferrer">
                Download Warranty PDF
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* MATERIALS QUOTE FORM */}
      <section className="border-b border-neutral-200 bg-white py-24">
        <div className="container mx-auto grid gap-10 px-6 lg:grid-cols-2 lg:items-start lg:px-12">
          <div>
            <p className="mb-3 font-mono text-xs uppercase tracking-[0.3em] text-amber-600">Materials Quote</p>
            <h2 className="font-serif text-3xl font-bold text-neutral-900 sm:text-4xl">
              Tell us your materials, MOQ & timeline.
            </h2>
            <div className="mt-3 h-px w-16 bg-amber-500" />
            <p className="mt-4 max-w-md text-neutral-600 leading-relaxed">
              Send us the exact product, finish/color, quantity range and project timeline. Our team
              replies with factory-direct MOQ pricing, lead times and OEM options within 1 business day.
            </p>
            <ul className="mt-6 space-y-3 text-sm text-neutral-700">
              <li className="flex gap-2"><span className="text-amber-600">→</span> Solid surface (stone resin) — non-porous, repairable</li>
              <li className="flex gap-2"><span className="text-amber-600">→</span> Custom RAL colors & textured finishes available</li>
              <li className="flex gap-2"><span className="text-amber-600">→</span> EU sea freight (30–40 days) or air freight (10–15 days)</li>
              <li className="flex gap-2"><span className="text-amber-600">→</span> CE certified · 10-year structural warranty</li>
            </ul>
          </div>
          <MaterialsQuoteForm />
        </div>
      </section>

      {/* LIFESTYLE */}
      <section className="border-b border-neutral-200 bg-white py-24">
        <div className="container mx-auto grid gap-10 px-6 lg:grid-cols-3 lg:px-12">
          <div className="lg:col-span-1">
            <p className="mb-3 font-mono text-xs uppercase tracking-[0.3em] text-amber-600">Environments</p>
            <h2 className="font-serif text-3xl font-bold text-neutral-900">
              Deliver a luxury experience without luxury construction costs.
            </h2>
            <div className="mt-3 h-px w-16 bg-amber-500" />
            <p className="mt-4 text-neutral-600 leading-relaxed">
              Specified for hotel-grade bathrooms, branded residences, and high-end real estate
              developments across Europe.
            </p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:col-span-2">
            <ReservedImageSlot
              src="/images/bath/lifestyle-suite.jpg"
              alt="luxury hotel bathroom suite freestanding bathtub"
              aspect="aspect-[4/3]"
              slotLabel="Lifestyle 1 — Hotel Suite"
            />
            <ReservedImageSlot
              src="/images/bath/lifestyle-residence.jpg"
              alt="modern bathroom design luxury residential development"
              aspect="aspect-[4/3]"
              slotLabel="Lifestyle 2 — Residence"
            />
          </div>
        </div>
      </section>

      {/* TRUST */}
      <section className="border-b border-neutral-200 bg-neutral-50 py-20">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="grid items-center gap-6 lg:grid-cols-3">
            <div className="flex items-center gap-4 border border-neutral-200 bg-white p-5">
              <ShieldCheck className="h-10 w-10 text-amber-600" />
              <div>
                <p className="font-serif text-lg font-semibold text-neutral-900">CE Certified</p>
                <p className="text-xs text-neutral-500">European compliance for solid surface sanitary ware</p>
              </div>
            </div>
            <div className="flex items-center gap-4 border border-neutral-200 bg-white p-5">
              <Building2 className="h-10 w-10 text-amber-600" />
              <div>
                <p className="font-serif text-lg font-semibold text-neutral-900">Used in International Developments</p>
                <p className="text-xs text-neutral-500">Trusted by developers across the EU and the Gulf</p>
              </div>
            </div>
            <div className="flex items-center gap-4 border border-neutral-200 bg-white p-5">
              <Truck className="h-10 w-10 text-amber-600" />
              <div>
                <p className="font-serif text-lg font-semibold text-neutral-900">European Logistics</p>
                <p className="text-xs text-neutral-500">Sea freight 30–40 days · Air freight 10–15 days</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* LEAD CAPTURE */}
      <section id="quote" className="bg-white py-24">
        <div className="container mx-auto grid gap-10 px-6 lg:grid-cols-2 lg:px-12">
          <div>
            <p className="mb-3 font-mono text-xs uppercase tracking-[0.3em] text-amber-600">Project Enquiry</p>
            <h2 className="font-serif text-3xl font-bold text-neutral-900 sm:text-4xl">
              Get a Quote for Your Project
            </h2>
            <div className="mt-3 h-px w-16 bg-amber-500" />
            <p className="mt-4 max-w-md text-neutral-600">
              Tell us about your development. A project specialist will respond within one business
              day with bulk pricing, lead times, and OEM options.
            </p>
            <div className="mt-8 space-y-3 text-sm text-neutral-600">
              <p>📧 <a className="text-amber-600 hover:underline" href="mailto:info@aureum7trading.com">info@aureum7trading.com</a></p>
              <p>📱 <a className="text-amber-600 hover:underline" href="https://wa.me/34642278939">+34 642 278 939 (WhatsApp)</a></p>
            </div>
          </div>
          <BathLeadForm />
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BathSanitary;
